import { FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import {
  ChartBarData,
  FilterKeys,
  GetInvoicesReportingAggregatedResponse,
  GetPayableFeesArrangeBy,
  GetPayableFeesQueryFilters,
  InvoicesReportingAggregatedRow,
  InvoicesReportingBreakDownBy,
  PayableFeeStatus,
} from '@schooly/api';
import { ArrangeByDropdown } from '@schooly/components/filters';
import { Currencies, CURRENCY_SYMBOLS, SchoolUserRole } from '@schooly/constants';
import { useSchoolProperties } from '@schooly/hooks/use-school-properties';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import { TooltipComponentFormatterCallbackParams } from 'echarts';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { renderToString } from 'react-dom/server';
import { FormattedMessage, useIntl } from 'react-intl';

import { BarSelectProps, ChartBar, ChartBarProps } from './chartBar/ChartBar';
import { ChartsContainer } from './chartBar/ChartsContainer';
import { ChartTooltip } from './chartBar/ChartTooltip';
import { useGetLabel } from './chartBar/useGetLabel';
import { HSBColor } from './chartBar/utils';
import { useCharts } from './context/ChartsContext';
import {
  numberFormatter,
  payableFeesAxisValueFormatter,
  payableFeesSeriesLabelFormatter,
  payableFeesStatusColorPalette,
} from './utils';

const CHART_FEE_STATUS_SORT: PayableFeeStatus[] = [
  'paid',
  'partially_paid',
  'due',
  'unpaid',
  'overdue',
  'projected',
];

export type PayableFeesChartsCustomGrid<T> = {
  row: InvoicesReportingAggregatedRow;
  currency: Currencies;
  filters: Partial<T>;
  totalCount?: number;
};

interface ChartsProps extends Omit<ChartBarProps, 'chartData' | 'getDataName'> {
  entityType: 'student' | 'staff';
  onOpenCustomGrid: (v: PayableFeesChartsCustomGrid<GetPayableFeesQueryFilters> | null) => void;
  onChangeBreakDownBy: (v: InvoicesReportingBreakDownBy) => void;
  breakDownByOptions: InvoicesReportingBreakDownBy[];
  arrangeBy: GetPayableFeesArrangeBy;
  breakDownBy: InvoicesReportingBreakDownBy;
  filters: GetPayableFeesQueryFilters;
  query?: string;
  data?: GetInvoicesReportingAggregatedResponse;
  loading?: boolean;
}

export const PayableFeesChart: FC<ChartsProps> = ({
  breakDownByOptions,
  entityType,
  filters,
  onOpenCustomGrid,
  onChangeBreakDownBy,
  breakDownBy,
  arrangeBy,
  data,
  loading: loadingData,
}) => {
  const { $t, formatMessage } = useIntl();
  const {
    isChartsOpened,
    showZeroValues,
    schoolId,
    chartSelectedElement,
    setChartSelectedElement,
    onToggleChartsOpened,
    onToggleZeroValues,
  } = useCharts();

  const chartTooltip = useCallback((params: TooltipComponentFormatterCallbackParams): string => {
    if (Array.isArray(params)) {
      throw new Error('Array is not accepted in renderTooltip');
    }

    if (!params.value) {
      return '';
    }

    const seriesName = params.seriesName ?? '';
    return renderToString(
      <ChartTooltip
        title={seriesName}
        value={numberFormatter(params.value as number)}
        backgroundColor={typeof params.color === 'string' ? params.color : undefined}
      />,
    );
  }, []);

  const { schoolProperties, isLoading: isLoadingSchoolProperties } = useSchoolProperties({
    userType: entityType === 'student' ? SchoolUserRole.Student : SchoolUserRole.Staff,
    schoolId,
    showReEnrollmentProperties: true,
  });

  const getDataName = useGetLabel({
    arrangeBy,
    schoolProperties,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  const getBarName = useCallback(
    (name: string) => {
      switch (arrangeBy) {
        case FilterKeys.Month:
          try {
            return format(newDateTimezoneOffset(name), "MMM''yy");
          } catch (_error) {
            return name;
          }
        case FilterKeys.Product:
          return name;
        case FilterKeys.DayPastDue:
          return formatMessage({ id: `payableFees-${name}` });
        default:
          return name;
      }
    },
    [arrangeBy, data?.rows?.length],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  useEffect(() => {
    setChartSelectedElement(undefined);
    onOpenCustomGrid(null);
  }, [arrangeBy, filters, setChartSelectedElement, onOpenCustomGrid]);

  const {
    normalizedData: chartData,
    hasZeroValues,
    customColorPalette,
  } = useMemo(() => {
    if (!data?.chartData.length) return {};

    let hasZeroValues = false;

    const arrangeByMapper: Record<number, boolean> = [];

    const arrangeByData = {
      dataKey: arrangeBy,
      data: data?.chartData?.[0]?.rows?.map(({ name, value }) => {
        return {
          name: getBarName(arrangeBy === FilterKeys.Month ? value : name),
          dataValue: value,
        };
      }),
    };

    const seriesData = data.chartData.map(({ value, name, rows }) => {
      return {
        name,
        dataValue: value,
        dataKey: breakDownBy,
        data: rows.map(({ total }) => total),
      };
    });

    const arrangeByModified = arrangeByData.data?.filter((item, index) => {
      const total = seriesData.reduce((prev, curr) => prev + (curr.data[index] ?? 0), 0);

      if (total === 0) {
        hasZeroValues = true;
      }

      if (!showZeroValues && !total) {
        arrangeByMapper[index] = false;
        return false;
      }

      arrangeByMapper[index] = true;
      return true;
    });

    const normalizedData: ChartBarData = {
      arrange_by: {
        ...arrangeByData,
        data: arrangeByModified ?? [],
      },
      series: (breakDownBy === FilterKeys.FeeStatus
        ? seriesData.sort(
            (a, b) =>
              CHART_FEE_STATUS_SORT.indexOf(a.dataValue as PayableFeeStatus) -
              CHART_FEE_STATUS_SORT.indexOf(b.dataValue as PayableFeeStatus),
          )
        : seriesData
      ).map((item) => {
        return {
          ...item,
          data: item.data.filter((d, idx) => {
            return arrangeByMapper[idx];
          }),
        };
      }),
    };

    return {
      normalizedData,
      hasZeroValues,
      customColorPalette: normalizedData.series
        .map(({ dataValue }) => {
          return payableFeesStatusColorPalette.find(({ key }) => dataValue === key)?.color;
        })
        .filter((el) => !!el) as HSBColor[],
    };
  }, [arrangeBy, data, getBarName, showZeroValues, breakDownBy]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  const onBarSelect = useCallback(
    (params: BarSelectProps | null) => {
      if (!params) {
        setChartSelectedElement((old) => {
          if (old) onOpenCustomGrid(null);
          return undefined;
        });
        return;
      }

      const { data: barData, bar } = params;

      const getFiltersFromBarData = (
        barData: BarSelectProps['data'],
      ): Partial<GetPayableFeesQueryFilters> => {
        const filters: Partial<Record<FilterKeys, any>> = {};
        if (!barData) return filters;

        for (const { dataKey, dataValue } of barData) {
          if (!dataKey) {
            return filters;
          }

          filters[dataKey] = [dataValue === null ? '' : dataValue];
        }

        return filters;
      };

      setChartSelectedElement((old) => {
        const isElementSelected =
          old?.dataIndex === bar.dataIndex && old?.seriesIndex === bar.seriesIndex;
        const newSelectedBar = isElementSelected ? undefined : bar;

        if (newSelectedBar && barData && data) {
          const barFilters = getFiltersFromBarData(barData);

          const selectedRow = data?.rows.find(
            ({ value, name }) =>
              getBarName((arrangeBy === FilterKeys.Month ? value : name) as string) ===
              newSelectedBar.name,
          );

          if (selectedRow) {
            onOpenCustomGrid({
              filters: { ...filters, ...barFilters },
              currency: data.currency,
              row: selectedRow,
              totalCount: 0, // provide zero value to override total while loading count in
            });
          }
        } else {
          onOpenCustomGrid(null);
        }

        return newSelectedBar;
      });
    },
    [filters, data, onOpenCustomGrid, setChartSelectedElement],
  );

  const legendData = useMemo(
    () => chartData?.series?.filter((s) => s.data.some(Boolean)).map(getDataName) ?? [],
    [chartData?.series, getDataName],
  );

  if (!isChartsOpened) return null;

  return (
    <ChartsContainer
      isEmpty={!!chartData?.series.every((s) => s.data.length === 0)}
      isLoading={loadingData || isLoadingSchoolProperties}
      onToggleChartsOpened={onToggleChartsOpened}
      arrangeBy={arrangeBy}
      zeroValuesToggler={
        hasZeroValues ? (
          <FormControlLabel
            control={<Switch checked={showZeroValues} onChange={onToggleZeroValues} />}
            label={<FormattedMessage id="showZeroValues" />}
            sx={{
              m: 0,
              color: showZeroValues ? 'text.primary' : 'text.secondary',
            }}
          />
        ) : null
      }
      arrangeByDropdown={
        arrangeBy ? (
          <ArrangeByDropdown
            onSelectOption={onChangeBreakDownBy}
            options={breakDownByOptions?.filter((o) => o !== arrangeBy)}
            selectedOption={breakDownBy}
            label={$t({ id: 'brakeDownBy' })}
          />
        ) : undefined
      }
      totalLabel={
        data?.total ? (
          <Stack direction="row" gap={1}>
            <Typography variant="h3" sx={{ fontSize: 15 }} color="common.grey2">
              Total:
            </Typography>

            <Typography variant="h3" sx={{ fontSize: 15 }} color="primary.main">
              {CURRENCY_SYMBOLS[data.currency]} {numberFormatter(data.total)}
            </Typography>
          </Stack>
        ) : undefined
      }
    >
      {chartData && customColorPalette && (
        <ChartBar
          key={breakDownBy}
          renderTooltip={chartTooltip}
          chartData={chartData}
          onBarSelect={onBarSelect}
          getDataName={getDataName}
          selectedBar={chartSelectedElement}
          legendData={legendData}
          showZeroValues={showZeroValues}
          customColorPalette={customColorPalette}
          customAxisValueFormatter={payableFeesAxisValueFormatter}
          customSeriesLabelFormatter={payableFeesSeriesLabelFormatter}
        />
      )}
    </ChartsContainer>
  );
};
